import { Component, inject, computed } from '@angular/core';
import { CommonModule, JsonPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ConfigStore, ArgoConfig, validateConfig } from '@argo/config';
import { CommonPhase } from '@argo/common';
import { 
  ScaModuleTitleComponent,
  ScaCardWorkshopComponent,
  ScaBadgeComponent,
  ScaAlertComponent,
  ScaIconComponent,
  ScaButtonComponent,
  ScaClaimCardComponent,
  ScaViewMoreComponent,
  ScaCardCategoryModule
} from '@argo-dev-components-sca-angular/dev-components-sca';

@Component({
  selector: 'app-config-test-complete',
  standalone: true,
  imports: [
    CommonModule, 
    JsonPipe, 
    FormsModule, 
    ScaModuleTitleComponent,
    ScaCardWorkshopComponent,
    ScaBadgeComponent,
    ScaAlertComponent,
    ScaIconComponent,
    ScaButtonComponent,
    ScaClaimCardComponent,
    ScaViewMoreComponent,
    ScaCardCategoryModule
  ],
  templateUrl: './config-test-complete.component.html',
  styleUrl: './config-test-complete.component.scss'
})
export class ConfigTestCompleteComponent {
  readonly config = inject(ConfigStore);
  readonly CommonPhase = CommonPhase;
  
  readonly coverage = 100;
  
  // Textos para sca-view-more
  readonly currentConfigCollapsed = 'Ver Config Actual';
  readonly currentConfigExpanded = 'Ocultar Config Actual';
  readonly addRuleCollapsed = 'Ver Ejemplo de Código';
  readonly addRuleExpanded = 'Ocultar Ejemplo';
  readonly interceptorCollapsed = 'Ver Código de Uso';
  readonly interceptorExpanded = 'Ocultar Código';
  readonly jsonCollapsed = 'Ver JSON Completo (' + JSON.stringify(this.config.getConfig()).length + ' caracteres)';
  readonly jsonExpanded = 'Ocultar JSON';
  
  appConfig = this.config.getConfig();
  isLoading = false;
  loadResult: any = null;
  currentConfig: any = null;
  newConfigPath = 'assets/config/architecture-conf/';
  pathChanged = false;
  validationResult: { success: boolean; error?: string } | null = null;
  showRuleExample = false;
  channel = this.getChannelType();
  jsonSize = JSON.stringify(this.appConfig).length;

  async loadConfig() {
    this.isLoading = true;
    this.loadResult = null;
    try {
      this.loadResult = await this.config.loadWithFetch();
      this.appConfig = this.config.getConfig();
      this.jsonSize = JSON.stringify(this.appConfig).length;
    } catch (error) {
      this.loadResult = { error: String(error) };
    } finally {
      this.isLoading = false;
    }
  }

  showCurrentConfig() {
    this.currentConfig = this.config.getConfig();
  }

  changeConfigPath() {
    this.config.setConfigPath(this.newConfigPath);
    this.pathChanged = true;
    setTimeout(() => this.pathChanged = false, 3000);
  }

  runValidation() {
    try {
      validateConfig(this.appConfig);
      this.validationResult = { success: true };
    } catch (error: any) {
      this.validationResult = {
        success: false,
        error: error.message
      };
    }
  }

  showAddRuleExample() {
    this.showRuleExample = !this.showRuleExample;
  }

  readonly addRuleExampleCode = `addValidationRule({
  key: 'customField',
  validate: (config) => !!config.customField,
  errorMessage: 'El campo customField es obligatorio.'
});`;

  readonly interceptorUsageCode = `// app.config.ts
import { ApplicationConfig } from '@angular/core';
import { provideHttpClient } from '@angular/common/http';
import { withArgoInterceptors } from '@argo/common';
import { provideArgoConfig, ENV_CONFIG } from '@argo/config';
import { provideSecurityConfig } from '@argo/security';
import { provideArgoTraces } from '@argo/traces';
import { provideArgoAuth } from '@argo/authentication';
import { provideMultilanguage } from '@argo/multilanguage';
import { environment } from '../environments/environment';

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: ENV_CONFIG, useValue: environment },
    
    // withArgoInterceptors() registra automáticamente
    // todos los interceptores de las librerías Argo
    provideHttpClient(
      withArgoInterceptors()
    ),
    
    // Los providers registran sus interceptores automáticamente
    provideArgoConfig(),        // Carga config desde assets
    provideSecurityConfig(),    // Configura security
    provideArgoTraces(),        // Configura traces
    provideArgoAuth({ browserStorage: 'session' }),
    provideMultilanguage({
      supportedLangs: ['es-ES', 'en-US', 'pt-BR'],
      defaultLang: 'es-ES'
    })
  ]
};`;

  private getChannelType(): string {
    const regexDevices = /Android|BlackBerry|iPhone|iPad|iPod|Opera Mini|IEMobile/i;
    if (typeof window !== 'undefined') {
      return window.navigator.userAgent.match(regexDevices) ? 'MOBILE' : 'WEB';
    }
    return 'SSR';
  }
}
